/**
 * Ted #28 (#1050) — the ONE registry of the visual flags that follow a dog
 * through every stage of its stay.
 *
 * Ted (HTHD, 2026-09-21): "The larger goal is to have important information
 * visually follow the dog through every stage of their stay rather than
 * relying on someone opening notes and finding it."
 *
 * Before this file, four separate chip implementations (the dog-first strip,
 * the profile header, the kennel VaxChip, the printed arrivals board) each
 * decided for themselves what a dog's indicators were, and a new flag meant
 * remembering seven screens. This is the `dog-fields.ts` move (#621) applied
 * to indicators: derive the flags ONCE here, carry them on every payload that
 * already carries a dog card, render them with one component. A surface that
 * renders dog names without the flags fails a test, not a code review.
 *
 * SCOPE MATTERS. Transport is a property of the STAY (the same dog comes by
 * van Tuesday and with its owner Friday); intact / meds / vaccines belong to
 * the DOG; balance, credit and the signed agreement to the ACCOUNT. Phase 1
 * carried the stay scope and transport; Phase 2 (this file) folds the dog-
 * and account-level indicators in, with Gingr's per-condition alert moments.
 *
 * Ted's Gingr notes (2026-09-21, `03-ted-gingr-icons-explained.md`):
 *  - red `$` under the OWNER: "anything to do with the money … would follow
 *    underneath the client's name" → `scope: 'account'`;
 *  - vaccine shield yellow (close to expiring) / red (due or past due) under
 *    the DOG → `scope: 'dog'`;
 *  - "the employee-facing flag could say INTACT and be very obvious … two
 *    puppies … I initially assumed they had already been spayed/neutered …
 *    Important thing here is … not assume" → the INTACT pill, plus Gingr's
 *    "Altered by month: 6" threshold so a young puppy with no answer is
 *    neither flagged nor assumed;
 *  - Gingr warns at reservation creation for an unsigned agreement or an
 *    intact dog → `alertAt: ['creation']`.
 *
 * Ted 09-24 / 09-29 (lane LC, 2026-09-30) — check-in is the LOUD moment and
 * checkout stays quiet. Three more keys: `grooming` (Jorge's whiteboard —
 * "if any grooming service is attached initially or added later, having that
 * show prominently at check-in"), `banned` (Gingr's Do Not Admit — "should be
 * a hard stop", enforced by `checkInBooking` as 409 `do_not_admit`) and
 * `isolated` (a female in heat mid-stay — "immediately isolated and the
 * owner or emergency contact contacted to arrange pickup").
 *
 * Pure: no I/O, no Prisma. The server derives, the client renders.
 */
import type { AlteredStatus, VaxStatus } from './dog-badge-status.js';
import { isOlderThanMonths } from './dog-age.js';
import { lockboxNamesOf, matchMedicationList } from './medication-policy-match.js';

export const TRANSPORT_MODES = ['none', 'arrival', 'departure', 'both'] as const;
export type TransportMode = (typeof TRANSPORT_MODES)[number];

/** Anything the DB or a client hands us → a mode, never a throw. Unknown ⇒ 'none'. */
export function normalizeTransport(raw: string | null | undefined): TransportMode {
  const v = (raw ?? '').trim().toLowerCase();
  return (TRANSPORT_MODES as ReadonlyArray<string>).includes(v)
    ? (v as TransportMode)
    : 'none';
}

/**
 * The van brings the dog IN. Ted's rule: the dog's presence on the day list
 * proves nothing; a human confirms it is physically here before check-in
 * completes. 'departure' alone never gates check-in — the owner brought the
 * dog and the van only takes it home.
 */
export function transportRequiresArrivalCheck(mode: TransportMode): boolean {
  return mode === 'arrival' || mode === 'both';
}

/** The van takes the dog HOME — the flag the checkout surfaces care about. */
export function transportOnDeparture(mode: TransportMode): boolean {
  return mode === 'departure' || mode === 'both';
}

/** Desk copy. Short enough for a pill, unambiguous about direction. */
export const TRANSPORT_LABEL: Record<Exclude<TransportMode, 'none'>, string> = {
  arrival: 'Transport in',
  departure: 'Transport home',
  both: 'Transport both ways',
};

export type DogFlagScope = 'dog' | 'stay' | 'account';
/** Maps to the status tokens — never a brand colour, never colour alone. */
export type DogFlagSeverity = 'danger' | 'warn' | 'info';

/**
 * Every flag the registry can raise. A key is stable: tests and `data-testid`s
 * switch on it, and a client that meets a key it has not learned still draws
 * the pill from `label` — the label is the information.
 *
 *  - `transport`       stay    · the van (Phase 1)
 *  - `vax`             dog     · shots lapsed / lapsing / none on file
 *  - `intact`          dog     · not spayed/neutered, past the puppy threshold
 *  - `altered_unknown` dog     · no spay/neuter answer on file, past the threshold
 *  - `meds`            dog     · has medications (structured or notes)
 *  - `vax_unverified`  dog     · shots on file, none verified by staff (kennel roster only)
 *  - `visit_unpaid`    account · THIS visit has open invoices (drawn under the owner)
 *  - `balance`         account · the owner owes money (on a visit row: on OTHER visits)
 *  - `credit`          account · the owner holds store credit
 *  - `agreement`       account · no signed agreement on file
 *  - `grooming`        stay    · grooming is on this visit (own service, combo, or a leg)
 *  - `banned`          dog     · Do Not Admit — check-in is REFUSED (409 `do_not_admit`)
 *  - `isolated`        stay    · isolated from the floor mid-stay (in heat)
 *  - `lockbox`         dog     · a medication on the tenant's lockbox list (Sawyer 10 customer-booking-verify-policy (C6))
 */
export const DOG_FLAG_KEYS = [
  'transport',
  'vax',
  'intact',
  'altered_unknown',
  'meds',
  'vax_unverified',
  'visit_unpaid',
  'balance',
  'credit',
  'agreement',
  'grooming',
  'banned',
  'isolated',
  // Sawyer 10 customer-booking-verify-policy (C6) — appended last so no other lane renumbers.
  'lockbox',
] as const;
export type DogFlagKey = (typeof DOG_FLAG_KEYS)[number];

/**
 * Gingr's three per-condition alert toggles (Settings → System Icons:
 * "Reservation creation alert · Reservation Checkin Alert · Reservation
 * Checkout Alert"). A flag lists the moments a surface should raise it as a
 * prompt, not merely draw it. Everything here is NON-BLOCKING until Ted says
 * otherwise; the one hard stop is transport's arrival check
 * (`requiresArrivalCheck`), which is a 409, not an alert.
 */
export const DOG_FLAG_ALERT_MOMENTS = ['creation', 'checkin', 'checkout'] as const;
export type DogFlagAlertMoment = (typeof DOG_FLAG_ALERT_MOMENTS)[number];

export interface DogFlag {
  /** Stable machine key — what tests and `data-testid`s switch on. */
  key: DogFlagKey;
  scope: DogFlagScope;
  severity: DogFlagSeverity;
  /** The pill text. Colour-independent by construction. */
  label: string;
  /** Tooltip / aria detail — the sentence a new receptionist needs. */
  detail: string;
  /**
   * The check-in gate. True ⇒ `checkInBooking` refuses until the desk
   * confirms the dog is physically present (`arrival_unverified`).
   */
  requiresArrivalCheck: boolean;
  /** When a surface should raise this flag as a prompt (never a refusal). */
  alertAt: DogFlagAlertMoment[];
}

export interface StayFlagInput {
  transport: string | null | undefined;
  /** When the desk already confirmed this arrival (null/undefined = not yet). */
  arrivalVerifiedAt?: Date | string | null;
  /**
   * The visit's own open invoices, in cents (the row's `balanceDueCents`).
   * MONEY: the caller passes it only when the viewer may see money (#537) —
   * `null`/omitted raises no `visit_unpaid` and leaves `balance` as the
   * owner's total.
   */
  balanceDueCents?: number | null;
  /**
   * Ted 09-29 — grooming is on this visit: the visit IS a grooming
   * appointment, it is the boarding half of a boarding+grooming combo, or a
   * grooming service leg hangs off it (added at creation OR later). True ⇒
   * the `grooming` flag; the caller decides (`grooming-on-visit.ts`), the
   * registry never guesses from a service name it cannot see.
   */
  grooming?: boolean | null;
  /**
   * Ted 09-29 (#1111 AC6) — when the desk isolated this stay from the
   * floor, and why (`'in_heat'` today). Set ⇒ the `isolated` DANGER flag on
   * every surface that draws the stay; null ⇒ nothing.
   */
  isolatedAt?: Date | string | null;
  isolatedReason?: string | null;
}

/**
 * The DOG-scope facts, already derived by `dog-badge-status.ts` (vax level,
 * altered status) so the registry can never disagree with the strip's glyphs.
 */
export interface DogScopeFlagInput {
  vax?: VaxStatus | null;
  /** `deriveAlteredStatus(...)` — null means genuinely unknown, never guessed. */
  altered?: AlteredStatus | null;
  /** `Dog.birthDate` — the "Altered by month" threshold needs an age. */
  birthDate?: Date | string | null;
  /** Active structured meds OR free-text med instructions on file. */
  hasMedications?: boolean | null;
  /**
   * Johnny, 2026-09-23 — the kennel floor's "Unverified" vaccine state, which
   * Phase 2 dropped when it retired KennelHome's `VaxChip`. True ⇒ the dog has
   * shots on file and the roster's own aggregate (`kennel-service.ts`,
   * expired ≻ unverified ≻ expiring) reads `unverified`, so the card agrees
   * with the header count. ONLY the kennel roster passes it: Gingr imports
   * never set `verified`, so nearly every HTHD record is unverified and the
   * pill would flood any other screen. Absent ⇒ no flag.
   */
  vaxUnverified?: boolean | null;
  /**
   * Ted 09-24 ("Do Not Admit should be a hard stop") — `Dog.isBanned` and
   * its reason. True ⇒ the `banned` DANGER flag at creation and check-in,
   * and `checkInBooking` refuses the stay outright. The switch that sets it
   * is the dog profile's (#1116 AC5); this is only the read.
   */
  isBanned?: boolean | null;
  bannedReason?: string | null;
  /**
   * Sawyer 10 customer-booking-verify-policy (C6) — the medication facts the LOCKBOX rule matches
   * against `DogFlagRules.lockboxMedications`: active structured medication
   * names and the free-text notes. A surface that did not load names passes
   * `[]` (a notes-only match still raises it); absent ⇒ no LOCKBOX, ever.
   * The run card passes THIS STAY's effective notes (#1218), not the profile's.
   */
  medicationNames?: readonly string[] | null;
  medicationNotes?: string | null;
}

/**
 * Whether the owner has signed the tenant's agreement. `unknown` (and an
 * omitted field) NEVER raises a flag: the read may have failed, and telling
 * the desk "no agreement" about a customer who signed is the wrong lie.
 */
export type AgreementStanding = 'signed' | 'unsigned' | 'not_required' | 'unknown';

export interface AccountFlagInput {
  /** The owner's open balance across this tenant's open invoices, in cents. */
  balanceDueCents?: number | null;
  /** The owner's wallet balance (store credit), in cents. */
  storeCreditCents?: number | null;
  agreement?: AgreementStanding | null;
}

export interface DogFlagRules {
  /**
   * Gingr's "Altered by month: N" — the unaltered icon shows once a pet is
   * older than N months. Below it a puppy is neither flagged INTACT nor
   * asked about: too young for the question to mean anything at the desk.
   * Owner-configurable is Phase 3 (#933); the platform default is Gingr's.
   */
  alteredByMonths?: number;
  /**
   * Sawyer 10 customer-booking-verify-policy (C6) — the tenant's lockbox list (Settings → Operations →
   * Care details at booking). A dog whose medication matches raises LOCKBOX.
   * Empty / absent (the default) ⇒ no flag on any surface.
   */
  lockboxMedications?: readonly string[] | null;
}

/** Gingr's shipped default, and Ted's screenshot: "Altered by month: 6". */
export const ALTERED_BY_MONTHS_DEFAULT = 6;

export interface DeriveDogFlagsInput {
  /** The booking the card is rendered FOR. Absent on stay-less surfaces (the ID card). */
  stay?: StayFlagInput | null;
  /** The dog's own facts. Absent when the surface has not loaded them. */
  dog?: DogScopeFlagInput | null;
  /** The owner's account. Absent for money-blind roles and stay-less reads. */
  account?: AccountFlagInput | null;
  rules?: DogFlagRules | null;
  /** Clock for the age threshold; defaults to now. */
  now?: Date;
}

/** Whole months from `birthDate` to `now`, or null when not derivable. */
export function ageInMonths(
  birthDate: Date | string | null | undefined,
  now: Date = new Date(),
): number | null {
  if (birthDate === null || birthDate === undefined || birthDate === '') return null;
  const born = birthDate instanceof Date ? birthDate : new Date(birthDate);
  if (Number.isNaN(born.getTime())) return null;
  let months =
    (now.getUTCFullYear() - born.getUTCFullYear()) * 12 +
    (now.getUTCMonth() - born.getUTCMonth());
  if (now.getUTCDate() < born.getUTCDate()) months -= 1;
  return months < 0 ? null : months;
}

function formatMoney(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

/**
 * Severity-first, then a fixed order inside a severity: the safety case
 * (an unverified transported arrival) reads before anything else at 375px
 * where only one pill may fit, and money reads after the dog's own flags.
 *
 * ONE ranking for every surface: the server builds with it, the lodging
 * block folds with it and the admin app sorts its alert panel with it
 * (#1067 review: those were three hand-copied tables).
 */
export const DOG_FLAG_SEVERITY_RANK: Readonly<Record<DogFlagSeverity, number>> = { danger: 0, warn: 1, info: 2 };
const KEY_RANK: Readonly<Record<DogFlagKey, number>> = {
  // The two refusals read first: a dog that may not come in, and a dog that
  // may not be on the floor. Then the arrival check, then everything else.
  banned: 0,
  isolated: 1,
  transport: 2,
  vax: 3,
  intact: 4,
  // Sawyer 10 customer-booking-verify-policy (C6) — after INTACT, before Grooming and paperwork; fractional so
  // no other lane's keys are renumbered.
  lockbox: 4.5,
  grooming: 5,
  agreement: 6,
  visit_unpaid: 7,
  balance: 8,
  altered_unknown: 9,
  meds: 10,
  vax_unverified: 11,
  credit: 12,
};

/**
 * The shape the ranking helpers need. Structural on purpose: the admin app's
 * wire type allows a key a NEWER server sent that this bundle has not
 * learned, and an `alertAt` an older server omitted — both must sort and
 * filter without throwing.
 */
export interface RankableFlag {
  key: string;
  severity: DogFlagSeverity;
  alertAt?: ReadonlyArray<string>;
}

/** An unknown key sorts after every known one inside its severity. */
function keyRank(key: string): number {
  return (KEY_RANK as Readonly<Record<string, number>>)[key] ?? Number.MAX_SAFE_INTEGER;
}

export function sortDogFlags<F extends RankableFlag>(flags: ReadonlyArray<F>): F[] {
  return [...flags].sort(
    (a, b) =>
      DOG_FLAG_SEVERITY_RANK[a.severity] - DOG_FLAG_SEVERITY_RANK[b.severity] ||
      keyRank(a.key) - keyRank(b.key),
  );
}

/**
 * The flags a surface should raise as a prompt at `moment`. A flag without
 * `alertAt` (an older server) never alerts — never a guessed prompt.
 */
export function flagsAlertingAt<F extends RankableFlag>(
  flags: ReadonlyArray<F> | null | undefined,
  moment: DogFlagAlertMoment,
): F[] {
  if (!Array.isArray(flags)) return [];
  return sortDogFlags(flags.filter((f) => Array.isArray(f.alertAt) && f.alertAt.includes(moment)));
}

/**
 * Several dogs' lists folded into ONE (the lodging block: a family on one
 * bed): one flag per key, the worst severity kept, in registry order. A
 * family block with one intact dog reads INTACT; which dog is one tap away.
 */
export function mergeFlagsByKey<F extends RankableFlag>(lists: Iterable<ReadonlyArray<F>>): F[] {
  const byKey = new Map<string, F>();
  for (const list of lists) {
    for (const f of list) {
      const have = byKey.get(f.key);
      if (!have || DOG_FLAG_SEVERITY_RANK[f.severity] < DOG_FLAG_SEVERITY_RANK[have.severity]) byKey.set(f.key, f);
    }
  }
  return sortDogFlags([...byKey.values()]);
}

/** The isolation reasons the desk can record, and the sentence each one reads as. */
export const ISOLATION_REASONS = ['in_heat'] as const;
export type IsolationReason = (typeof ISOLATION_REASONS)[number];

export function normalizeIsolationReason(raw: string | null | undefined): IsolationReason | null {
  const v = (raw ?? '').trim().toLowerCase();
  return (ISOLATION_REASONS as ReadonlyArray<string>).includes(v) ? (v as IsolationReason) : null;
}

const ISOLATED_DETAIL: Record<IsolationReason, string> = {
  in_heat:
    'In heat — isolated from the floor. No group time, no shared run; the owner is being contacted to arrange pickup.',
};

function stayFlags(stay: StayFlagInput): DogFlag[] {
  const out: DogFlag[] = [];
  // Ted 09-29 (#1111 AC6) — isolated reads BEFORE transport: a dog that may
  // not be on the floor outranks how it got to the building.
  if (stay.isolatedAt != null) {
    const reason = normalizeIsolationReason(stay.isolatedReason);
    out.push({
      key: 'isolated',
      scope: 'stay',
      severity: 'danger',
      label: 'ISOLATED',
      detail: reason
        ? ISOLATED_DETAIL[reason]
        : 'Isolated from the floor — no group time, no shared run. See the stay for why.',
      requiresArrivalCheck: false,
      // Loud at check-in (a re-check-in after undo) and visible at checkout
      // (the pickup Ted asked for ends this stay); the quiet-checkout rule
      // (`CheckoutInterrupt`) still pops ONLY money — this is decoration there.
      alertAt: ['checkin', 'checkout'],
    });
  }
  const mode = normalizeTransport(stay.transport);
  if (mode !== 'none') out.push(transportFlag(stay, mode));
  // Ted 09-29 — "If any grooming service is attached initially or added
  // later, having that show prominently at check-in should help prevent
  // anything from being missed and remind reception to put it on Jorge's
  // whiteboard." Check-in only: by checkout the grooming has happened.
  if (stay.grooming === true) {
    out.push({
      key: 'grooming',
      scope: 'stay',
      severity: 'warn',
      label: 'Grooming',
      detail: 'Grooming is on this visit — put it on the grooming board at check-in so nothing is missed.',
      requiresArrivalCheck: false,
      alertAt: ['checkin'],
    });
  }
  return out;
}

function transportFlag(stay: StayFlagInput, mode: Exclude<TransportMode, 'none'>): DogFlag {
  const needsCheck = transportRequiresArrivalCheck(mode);
  const verified = stay.arrivalVerifiedAt != null;
  const alertAt: DogFlagAlertMoment[] = [];
  if (needsCheck) alertAt.push('checkin');
  if (transportOnDeparture(mode)) alertAt.push('checkout');
  return {
    key: 'transport',
    scope: 'stay',
    // An unverified transported ARRIVAL is the safety case Ted wrote in
    // for; once verified (or when only the ride home is by van) it is
    // information, not a warning.
    severity: needsCheck && !verified ? 'warn' : 'info',
    label: TRANSPORT_LABEL[mode],
    detail:
      mode === 'departure'
        ? 'This dog goes home by transport — not with the owner at checkout.'
        : needsCheck && !verified
          ? 'This dog arrives by transport — confirm they are physically here before checking in.'
          : mode === 'both'
            ? 'Arrival confirmed. This dog goes home by transport too.'
            : 'Arrival by transport, confirmed at the desk.',
    requiresArrivalCheck: needsCheck && !verified,
    alertAt,
  };
}

/** Desk copy for the one refusal that has no override. */
export const DO_NOT_ADMIT_LABEL = 'DO NOT ADMIT';

export function doNotAdmitDetail(reason: string | null | undefined): string {
  const why = (reason ?? '').trim();
  return why.length > 0
    ? `Do not admit — ${why}. Check-in is refused; an owner or manager decides.`
    : 'Do not admit. Check-in is refused; an owner or manager decides.';
}

/**
 * Sawyer 10 customer-booking-verify-policy (C6) — the ONE LOCKBOX flag
 * constructor, shared by `dogFlags` and the run card's served-card pass
 * (apps/server/src/lib/run-card-lockbox.ts) so the two read identically.
 */
export function lockboxFlag(names: readonly string[]): DogFlag {
  return {
    key: 'lockbox',
    scope: 'dog',
    severity: 'warn',
    label: 'LOCKBOX',
    detail: `Lockbox medication on file: ${names.join(', ')}. It goes in the lockbox at check-in.`,
    requiresArrivalCheck: false,
    alertAt: ['checkin'],
  };
}

function dogFlags(dog: DogScopeFlagInput, rules: DogFlagRules, now: Date): DogFlag[] {
  const out: DogFlag[] = [];

  // Ted 09-24 — "any Do Not Admit warning should be very noticeable. Do Not
  // Admit should be a hard stop." The flag is the notice; the stop is
  // `checkInBooking`'s 409 `do_not_admit`, which this flag announces. The
  // reason rides in the sentence so the desk never has to open the profile.
  if (dog.isBanned === true) {
    out.push({
      key: 'banned',
      scope: 'dog',
      severity: 'danger',
      label: DO_NOT_ADMIT_LABEL,
      detail: doNotAdmitDetail(dog.bannedReason),
      requiresArrivalCheck: false,
      alertAt: ['creation', 'checkin'],
    });
  }

  // Vaccines — Ted's shield, the strip's syringe: yellow = close to expiring,
  // red = due or past due. `none` is attention, never a silent pass (#546).
  const vax = dog.vax ?? null;
  if (vax && vax.level !== 'ok') {
    out.push({
      key: 'vax',
      scope: 'dog',
      severity: vax.level === 'expired' ? 'danger' : 'warn',
      label:
        vax.level === 'expired'
          ? 'Vax expired'
          : vax.level === 'expiring'
            ? 'Vax expiring'
            : 'No vax records',
      detail: vax.detail,
      requiresArrivalCheck: false,
      // Gingr's vaccination icon alerts at creation and check-in; the #773
      // booking gate is the separate, tenant-configurable refusal.
      alertAt: ['creation', 'checkin'],
    });
  }

  // Spay/neuter — two different rules for two different facts (Ted 09-24 /
  // 09-29, via LD's row, folded in by lane LC 2026-09-30):
  //  · INTACT keeps Gingr's "Altered by month" threshold: below it a known-
  //    intact puppy is not shouted at — too young for the pill to mean
  //    anything at the desk.
  //  · "Spay/neuter?" (NO answer on file) is raised at ANY age. Ted dropped
  //    "the puppy rule" for the question: the note stays on an imported puppy
  //    until someone records the answer (#1116 AC2), never assumed altered
  //    (Ted's two puppies), never shouted as INTACT (it is not known).
  // The ONE month-arithmetic rule (`dog-age.ts`, #1116 / #1101): "older than
  // N months" is true only AFTER the N-month anniversary, clamped to month
  // end; an unknown birth date reads as past the threshold — asked, never
  // assumed altered.
  const threshold = rules.alteredByMonths ?? ALTERED_BY_MONTHS_DEFAULT;
  const pastThreshold = isOlderThanMonths(dog.birthDate, threshold, now) ?? true;
  if (dog.altered !== undefined) {
    if (dog.altered === 'intact' && pastThreshold) {
      out.push({
        key: 'intact',
        scope: 'dog',
        severity: 'warn',
        label: 'INTACT',
        detail: 'Not spayed/neutered. Do not assume — plan the group and the run accordingly.',
        requiresArrivalCheck: false,
        alertAt: ['creation', 'checkin'],
      });
    } else if (dog.altered === null) {
      out.push({
        key: 'altered_unknown',
        scope: 'dog',
        severity: 'info',
        label: 'Spay/neuter?',
        detail: 'No spay/neuter answer on file — ask the owner, never assume.',
        requiresArrivalCheck: false,
        alertAt: ['creation'],
      });
    }
  }

  if (dog.hasMedications === true) {
    out.push({
      key: 'meds',
      scope: 'dog',
      severity: 'info',
      label: 'Meds',
      detail: 'Has medications — see the run card for the protocol.',
      requiresArrivalCheck: false,
      alertAt: ['checkin'],
    });
  }

  // Sawyer 10 customer-booking-verify-policy (C6) — "certain medications have to be locked in a
  // lockbox too" (memo 3). Matched by the one shared matcher against the
  // tenant's lockbox list; raised at check-in so the med goes straight in.
  const lockboxList = rules.lockboxMedications ?? [];
  if (lockboxList.length > 0 && (dog.medicationNames != null || dog.medicationNotes != null)) {
    const names = lockboxNamesOf(
      matchMedicationList(lockboxList, {
        medicationNames: dog.medicationNames ?? [],
        medicationNotes: dog.medicationNotes ?? null,
      }),
    );
    if (names.length > 0) out.push(lockboxFlag(names));
  }

  // Quiet on purpose (Johnny, 2026-09-23): `info`, no alert moment — nearly
  // every Gingr-imported record is unverified, so this informs the floor
  // without competing with Vax expired or INTACT.
  if (dog.vaxUnverified === true) {
    out.push({
      key: 'vax_unverified',
      scope: 'dog',
      severity: 'info',
      label: 'Vax unverified',
      detail: 'Shots are on file but staff have not verified them yet — check the records.',
      requiresArrivalCheck: false,
      alertAt: [],
    });
  }

  return out;
}

/** A finite positive cent amount, or null. */
function positiveCents(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null;
}

/**
 * Johnny, 2026-09-23 — "both flags for the $". Two money questions, two keys,
 * so one can never hide the other (the Phase 2 bug: one `balance` key, two
 * meanings):
 *  - `visit_unpaid` — THIS visit's open invoices (the #546/#678 `$`), from
 *    the row the card is rendered for;
 *  - `balance` — what the owner owes. On a visit row it is the REST of it
 *    (owner total − this visit), because the owner total always includes this
 *    visit's own invoice and two pills repeating the same dollars read as a
 *    double charge. Stay-less surfaces (profile, ID card) keep the total.
 * Both render under the owner (`scope: 'account'`) — Ted: money "follows
 * underneath the client's name".
 */
function moneyFlags(account: AccountFlagInput | null, visitCents: number | null): DogFlag[] {
  const out: DogFlag[] = [];
  if (visitCents !== null) {
    out.push({
      key: 'visit_unpaid',
      scope: 'account',
      severity: 'danger',
      label: `This visit unpaid ${formatMoney(visitCents)}`,
      detail: `This visit has ${formatMoney(visitCents)} on open invoices — collect it at checkout.`,
      requiresArrivalCheck: false,
      alertAt: ['checkout'],
    });
  }
  const total = positiveCents(account?.balanceDueCents);
  const rest = total === null ? null : positiveCents(total - (visitCents ?? 0));
  if (rest !== null) {
    const onVisitRow = visitCents !== null;
    out.push({
      key: 'balance',
      scope: 'account',
      severity: 'danger',
      label: onVisitRow ? `Other invoices ${formatMoney(rest)}` : `Balance due ${formatMoney(rest)}`,
      detail: onVisitRow
        ? `This owner also owes ${formatMoney(rest)} on other visits' open invoices — collect before the next visit or at checkout.`
        : `This owner owes ${formatMoney(rest)} across open invoices — collect before the next visit or at checkout.`,
      requiresArrivalCheck: false,
      alertAt: ['checkin', 'checkout'],
    });
  }
  return out;
}

function accountFlags(account: AccountFlagInput): DogFlag[] {
  const out: DogFlag[] = [];
  const credit = account.storeCreditCents ?? 0;
  if (Number.isFinite(credit) && credit > 0) {
    out.push({
      key: 'credit',
      scope: 'account',
      severity: 'info',
      label: `Credit ${formatMoney(credit)}`,
      detail: `Store credit ${formatMoney(credit)} available — apply it at checkout.`,
      requiresArrivalCheck: false,
      alertAt: ['checkout'],
    });
  }
  if (account.agreement === 'unsigned') {
    out.push({
      key: 'agreement',
      scope: 'account',
      severity: 'warn',
      label: 'No signed agreement',
      detail: 'This owner has not signed the agreement — have it signed before the dog is on the floor.',
      requiresArrivalCheck: false,
      alertAt: ['creation', 'checkin'],
    });
  }
  return out;
}

/**
 * Ted 09-29 (#1111) — WHICH dogs get the "Is she in heat?" question. L0's
 * rule (issue comment, 2026-09-30), the SAME rule lane LG's reservation gate
 * asks with: the dog is not known to be male and not known to be spayed/
 * neutered — "the notice copy makes a needless question harmless, a skipped
 * one is not." NO age threshold: a known-intact puppy is asked at reservation
 * by LG, so she is confirmed at drop-off here too (L0, 2026-09-30: one door,
 * documented against the other). Gingr's "Altered by month" threshold governs
 * the INTACT pill, not this question.
 *
 * ONE function, two callers: the server has the row, the admin app has the
 * card — both carry `sex` and the derived `altered`, so there is nothing to
 * keep equivalent. `heatCheckAppliesFromCard` is the card-shaped name.
 */
export function heatCheckApplies(input: {
  sex: string | null | undefined;
  altered: AlteredStatus | null | undefined;
}): boolean {
  const sex = (input.sex ?? '').trim().toLowerCase();
  if (sex === 'male') return false;
  return !(input.altered === 'spayed' || input.altered === 'neutered' || input.altered === 'altered');
}

export function heatCheckAppliesFromCard(card: {
  sex: string | null | undefined;
  /** The card's derived altered status (`DogCard.altered`); absent reads as unknown ⇒ asked. */
  altered?: AlteredStatus | null;
}): boolean {
  return heatCheckApplies({ sex: card.sex, altered: card.altered ?? null });
}

/**
 * The flag list for one dog on one surface. Order is severity-first so the
 * most urgent pill reads first at 375px where only one may fit.
 */
export function deriveDogFlags(input: DeriveDogFlagsInput): DogFlag[] {
  const now = input.now ?? new Date();
  const rules = input.rules ?? {};
  const flags: DogFlag[] = [];
  if (input.stay) flags.push(...stayFlags(input.stay));
  if (input.dog) flags.push(...dogFlags(input.dog, rules, now));
  flags.push(...moneyFlags(input.account ?? null, positiveCents(input.stay?.balanceDueCents)));
  if (input.account) flags.push(...accountFlags(input.account));
  return sortDogFlags(flags);
}
